import React, { useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import {
  Block,
  Click,
  Col,
  Flex,
  Grid,
  Row,
  Spacer,
  Text,
  Toggle,
  useTheme,
} from 'vcc-ui';
import DocsNavigation from '../components/docs-navigation';
import { Footer } from '../components/footer';
import { Header } from '../components/header';

const headers = ['h1', 'h2', 'h3', 'h4'];
const TOCLink = ({ type, text, href }) => {
  const theme = useTheme();
  const ref = useRef();
  const [color, setColor] = useState(theme.color.foreground.primary);
  useEffect(() => {
    const handler = () => {
      if (window.location.hash === href) {
        setColor(theme.color.ornament.highlight);
        ref?.current?.scrollIntoView?.();
      } else {
        setColor('');
      }
    };
    handler();
    window.addEventListener('hashchange', handler);
    return () => {
      window.removeEventListener('hashchange', handler);
    };
  }, [theme, href]);
  return (
    <Text
      ref={ref}
      subStyle="inline-link"
      extend={() => ({
        paddingLeft: (Number(type?.replace('h', '')) - 1) * 12,
        textDecoration: 'none',
        ...(color && {
          color: `${color} !important`,
        }),
      })}
      href={href}
    >
      {text}
    </Text>
  );
};
export default function Layout({
  children,
  currentTheme,
  setCurrentTheme,
  pageTitle,
}) {
  const title = pageTitle || children[0].props.children;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const theme = useTheme();

  const tableOfContents = (
    <Block
      as="ul"
      extend={{
        position: 'fixed',
        paddingLeft: 22,
        overflowY: 'scroll',
        scrollBehavior: 'smooth',
        '::-webkit-scrollbar': {
          display: 'none',
        },
        top: 140,
        borderLeft: `1px solid ${theme.color.ornament.border}`,
        height: 'calc(100vh - 200px)',
        untilL: {
          display: 'none',
        },
      }}
    >
      {React.Children.map(children, (child) => {
        const type = child?.props?.mdxType;
        if (headers.includes(type)) {
          const href = `#${child?.props?.id}`;
          return (
            <Block as="li" extend={{ margin: 12 }}>
              <TOCLink type={type} href={href} text={child?.props?.children} />
            </Block>
          );
        }
        return null;
      })}
    </Block>
  );

  return (
    <Flex extend={{ background: theme.color.background.primary }}>
      <Head>
        <title>{title} - VCC UI Documentation</title>
      </Head>
      <Header activePage="docs" />
      <Grid>
        <Row>
          <Col size={2}>
            <DocsNavigation
              setMobileNavOpen={setMobileNavOpen}
              mobileNavOpen={mobileNavOpen}
            />
          </Col>
          <Col size={7}>
            <Click
              extend={{
                marginTop: '96px',
                display: 'none',
                padding: '4px 0px',
                [theme.breakpoints.untilL]: {
                  display: 'flex',
                },
                alignItems: 'center',
              }}
              onClick={() => {
                setMobileNavOpen(!mobileNavOpen);
              }}
            >
              <Block
                as="svg"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                extend={{
                  fill: theme.color.foreground.primary,
                }}
              >
                <path d="M3 18h12v-2H3v2zM3 6v2h18V6H3zm0 7h18v-2H3v2z" />
                <path fill="none" d="M0 0h24v24H0V0z" />
              </Block>
              <Spacer />
              <Text subStyle="inline-link">Docs → {title}</Text>
            </Click>
            <Spacer size={{ default: 0, fromL: 12 }} />
            <Flex extend={{ position: 'relative' }}>
              <Block
                extend={{
                  margin: '16px 0',
                  [theme.breakpoints.fromL]: {
                    position: 'absolute',
                    top: -12,
                    right: 0,
                  },
                }}
              >
                <Flex
                  extend={{
                    background: theme.color.background.secondary,
                    padding: 8,
                    [theme.breakpoints.fromL]: {
                      position: 'fixed',
                      right: 20,
                      zIndex: 1,
                      transform: 'translateX(-100%)',
                    },
                    flexDirection: 'row',
                  }}
                >
                  <Toggle
                    checked={currentTheme === 'dark'}
                    onChange={() =>
                      setCurrentTheme(
                        currentTheme === 'dark' ? 'light' : 'dark'
                      )
                    }
                  />
                  <Spacer />
                  <Text>Toggle theme</Text>
                </Flex>
              </Block>
              {children}
              <Spacer size={4} />
              <Footer />
            </Flex>
          </Col>
          <Col size={3}>
            <Spacer size={{ default: 4, [theme.breakpoints.fromL]: 8 }} />
            {tableOfContents}
          </Col>
        </Row>
      </Grid>
    </Flex>
  );
}
